import { useEffect, useRef, useState, type FormEvent } from "react";
import { loginWithEmail, loginWithGoogle, qaLogin, requestEmailOtp } from "./api";
import { saveSession } from "./session";
import { getAppState } from "../appState";

// The /auth screen. Email OTP is the primary path; Google Identity Services
// remains available when the server has GOOGLE_CLIENT_ID configured. On success
// we persist the session and return to ?returnTo=… (default "/").

interface GoogleCredentialResponse {
  credential: string;
}

interface GoogleIdApi {
  initialize(config: {
    client_id: string;
    callback: (res: GoogleCredentialResponse) => void;
  }): void;
  renderButton(parent: HTMLElement, options: Record<string, unknown>): void;
  prompt(): void;
}

declare global {
  interface Window {
    google?: { accounts: { id: GoogleIdApi } };
  }
}

const GSI_SRC = "https://accounts.google.com/gsi/client";

type EmailStep = "email" | "code";

function loadGsi(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GSI_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Failed to load Google sign-in.")));
      return;
    }
    const script = document.createElement("script");
    script.src = GSI_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Google sign-in."));
    document.head.appendChild(script);
  });
}

function returnTo(): string {
  const param = new URLSearchParams(window.location.search).get("returnTo");
  // Only allow same-site relative paths (avoid open-redirect).
  return param && param.startsWith("/") && !param.startsWith("//") ? param : "/";
}

/** After login: send users without a username to choose one before the game. */
function afterLogin(session: { user: { username?: string } }): void {
  if (!session.user.username) {
    const dest = encodeURIComponent(returnTo());
    window.location.replace(`/auth/username?returnTo=${dest}`);
    return;
  }
  window.location.replace(returnTo());
}

function otpErrorMessage(code: string): string {
  switch (code) {
    case "invalid":
      return "That code is incorrect. Try again.";
    case "expired":
      return "That code expired. Request a new one.";
    case "too_many_attempts":
      return "Too many attempts. Request a new code.";
    case "invalid_email":
      return "Enter a valid email address.";
    case "send_failed":
      return "Couldn't send the email. Try again shortly.";
    default:
      return "Sign-in failed. Please try again.";
  }
}

export function AuthPage() {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [googleReady, setGoogleReady] = useState(false);

  const [step, setStep] = useState<EmailStep>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [qaEnabled, setQaEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;

    // The Google client id comes from the server's /app-state (its
    // GOOGLE_CLIENT_ID), so it's configured in one place. Fetch it, then init GSI.
    // Missing Google config is fine — email OTP still works.
    getAppState()
      .then(({ googleClientId, qaLoginEnabled }) => {
        if (cancelled) return;
        // Server-owned non-prod toggle; the endpoint 404s when it's off.
        setQaEnabled(Boolean(qaLoginEnabled));
        if (!googleClientId) return;
        return loadGsi().then(() => {
          if (cancelled || !window.google || !buttonRef.current) return;
          window.google.accounts.id.initialize({
            client_id: googleClientId,
            callback: async (res) => {
              setBusy(true);
              setError(null);
              try {
                const session = await loginWithGoogle(res.credential);
                await saveSession(session);
                afterLogin(session);
              } catch {
                setError("Sign-in failed. Please try again.");
                setBusy(false);
              }
            },
          });
          window.google.accounts.id.renderButton(buttonRef.current, {
            theme: "filled_blue",
            size: "large",
            shape: "pill",
            text: "continue_with",
          });
          setGoogleReady(true);
        });
      })
      .catch(() => {
        // Google optional — don't block email OTP.
      });

    return () => {
      cancelled = true;
    };
  }, []);

  async function onRequestCode(e: FormEvent) {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Enter your email address.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await requestEmailOtp(trimmed);
      setStep("code");
      setCode("");
    } catch (err) {
      setError(otpErrorMessage(err instanceof Error ? err.message : ""));
    } finally {
      setBusy(false);
    }
  }

  async function onVerifyCode(e: FormEvent) {
    e.preventDefault();
    const trimmedCode = code.trim();
    if (!trimmedCode) {
      setError("Enter the code from your email.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session = await loginWithEmail(email.trim(), trimmedCode);
      await saveSession(session);
      afterLogin(session);
    } catch (err) {
      setError(otpErrorMessage(err instanceof Error ? err.message : ""));
      setBusy(false);
    }
  }

  /** Non-prod only: sign straight in with the entered email, skipping the OTP. */
  async function onQaLogin() {
    const trimmed = email.trim();
    if (!trimmed) {
      setError("Enter your email address.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const session = await qaLogin(trimmed);
      await saveSession(session);
      afterLogin(session);
    } catch (err) {
      setError(otpErrorMessage(err instanceof Error ? err.message : ""));
      setBusy(false);
    }
  }

  async function onResend() {
    setBusy(true);
    setError(null);
    try {
      await requestEmailOtp(email.trim());
      setCode("");
    } catch (err) {
      setError(otpErrorMessage(err instanceof Error ? err.message : ""));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <h1 className="auth-title">⚔️ Fortress</h1>
        <p className="auth-subtitle">Sign in to play.</p>

        {step === "email" ? (
          <form className="auth-email" onSubmit={onRequestCode}>
            <label className="auth-label" htmlFor="auth-email">
              Email
            </label>
            <input
              id="auth-email"
              className="auth-input"
              type="email"
              autoComplete="email"
              inputMode="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={busy}
              required
            />
            <button className="auth-submit" type="submit" disabled={busy}>
              {busy ? "Sending…" : "Send code"}
            </button>
            {qaEnabled && (
              <button
                className="auth-submit auth-submit--qa"
                type="button"
                disabled={busy}
                onClick={onQaLogin}
              >
                QA Login (no code)
              </button>
            )}
          </form>
        ) : (
          <form className="auth-email" onSubmit={onVerifyCode}>
            <p className="auth-hint">
              Code sent to <strong>{email.trim()}</strong>
            </p>
            <label className="auth-label" htmlFor="auth-code">
              Login code
            </label>
            <input
              id="auth-code"
              className="auth-input"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="6-digit code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              disabled={busy}
              required
              autoFocus
            />
            <button className="auth-submit" type="submit" disabled={busy}>
              {busy ? "Verifying…" : "Verify"}
            </button>
            <div className="auth-email-actions">
              <button
                type="button"
                className="auth-link"
                disabled={busy}
                onClick={onResend}
              >
                Resend code
              </button>
              <button
                type="button"
                className="auth-link"
                disabled={busy}
                onClick={() => {
                  setStep("email");
                  setCode("");
                  setError(null);
                }}
              >
                Change email
              </button>
            </div>
          </form>
        )}

        <div
          ref={buttonRef}
          className="auth-google"
          hidden={!googleReady}
          aria-busy={busy}
        />
        {busy && step === "email" && googleReady && (
          <p className="auth-hint">Working…</p>
        )}
        {error && <p className="auth-error">{error}</p>}
      </div>
    </div>
  );
}
